import { IPublicClientApplication, InteractionStatus, SilentRequest } from '@azure/msal-browser';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import LoadingScreen from '@components/LoadingScreen';
import MultiProvider from '@components/MultiProvider';
import DrawerSmall from '@components/drawer/DrawerSmall';
import MyDrawer from '@components/drawer/MyDrawer';
import useDrawer, { DrawerContext } from '@components/drawer/useDrawer';
import MyAppBar from '@components/header/MyAppBar';
import { pushError } from '@components/toast';
import { loginRequest } from '@configs/authConfig';
import { axiosClient } from '@controllers/axiosClient';
import useMedia from '@hooks/useMedia';
import { Box, Container, Stack } from '@mui/material';
import Login from '@pages/login';
import jwtDecode from 'jwt-decode';
import { Suspense, useLayoutEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

const ID_TOKEN_KEY = 'idToken';

const setupInterceptors = (instance: IPublicClientApplication, accessTokenRequest: SilentRequest, inProgress: InteractionStatus, getToken) => {
    axiosClient.interceptors.request.use(
        async (config) => {
            const token = await getToken(instance, accessTokenRequest);
            localStorage.setItem(ID_TOKEN_KEY, token);
            config.headers.Authorization = `Bearer ${token}`;
            return config;
        },
        (error) => Promise.reject(error)
    );

    axiosClient.interceptors.response.use(
        (response) => response,
        async (error) => {
            if (error?.response?.status === 401) {
                console.log(`System is not authenticated and handling 401 error...`, error);
                await handle401Error(error, instance, accessTokenRequest);
            } else if (error?.response?.status === 404 && error?.response?.data?.message === 'VRN not found') {
                return Promise.reject(error);
            } else {
                console.log(`System is not authenticated and pushing error...`, error);
                const errorMessage =
                    typeof error?.response?.data === 'string'
                        ? error?.response?.data
                        : error?.response?.data?.message || error.message || 'Server error!';
                pushError(errorMessage);
                return Promise.reject(error);
            }
        }
    );
};

const handle401Error = async (error, instance: IPublicClientApplication, accessTokenRequest: SilentRequest) => {
    const idToken = localStorage.getItem(ID_TOKEN_KEY);
    if (idToken) {
        const decoded: any = jwtDecode(idToken);
        const isValid = decoded.exp > Math.floor(Date.now() / 1000);
        if (!isValid) {
            pushError('Token has expired');
            await instance.acquireTokenRedirect(accessTokenRequest);
        } else {
            throw Error(error);
        }
    } else {
        await instance.acquireTokenRedirect(accessTokenRequest);
    }
};

const getToken = async (instance: IPublicClientApplication, accessTokenRequest: SilentRequest) => {
    try {
        const accessTokenResponse = await instance.acquireTokenSilent(accessTokenRequest);
        const _idToken = accessTokenResponse.idToken;
        return _idToken;
    } catch (error) {
        console.error(`getToken error: ${error}`);
        await instance.loginRedirect(loginRequest).catch((e) => {
            console.error(e);
        });
        return Promise.reject(error);
    }
};

function ProtectedChild() {
    const { instance, inProgress, accounts } = useMsal();

    const drawerContext = useDrawer();
    const { isMediumMD } = useMedia();

    const [openDrawer, setOpenDrawer] = useState<boolean>(true);

    useLayoutEffect(() => {
        const accessTokenRequest = {
            scopes: ['openid', 'profile', 'User.Read'],
            account: accounts[0],
        };
        setupInterceptors(instance, accessTokenRequest, inProgress, getToken);
    }, [instance, accounts, inProgress]);

    if (inProgress !== InteractionStatus.None) {
        return <LoadingScreen isLoading={true} />;
    }

    return (
        <MultiProvider providers={[<DrawerContext.Provider value={drawerContext} />]}>
            <MyAppBar isShowLabelAccount={true} isShowLabelLanguage={true} />
            <Stack direction={'row'} minHeight={'calc(100vh - 63px)'} width={'calc(100vW - 8px)'} className="App">
                {isMediumMD ? (
                    <DrawerSmall />
                ) : (
                    <MyDrawer
                        open={(e) => {
                            setOpenDrawer(e);
                        }}
                    />
                )}
                <Container
                    sx={{
                        pt: 14,
                        pb: 6,
                        pl: {
                            xs: isMediumMD ? undefined : !openDrawer ? 10 : 5,
                        },
                        pr: {
                            xs: isMediumMD ? undefined : !openDrawer ? 28 : 12,
                        },
                        minWidth: '100%',
                    }}
                >
                    <Suspense>
                        <Outlet />
                    </Suspense>
                </Container>
            </Stack>
        </MultiProvider>
    );
}

export function UnAuth() {
    return <Login />;
}

function AuthLayout() {
    return (
        <Box sx={{ overflowX: 'hidden', pt: 0 }}>
            <AuthenticatedTemplate>
                <ProtectedChild />
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <UnAuth />
            </UnauthenticatedTemplate>
        </Box>
    );
}

export default AuthLayout;
